import React, { useEffect, useState } from "react";
import moment from "moment";

import TableContainer from "@material-ui/core/TableContainer";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import TableHead from "@material-ui/core/TableHead";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Checkbox from "@material-ui/core/Checkbox";
import TableRow from "@material-ui/core/TableRow";
import Person from "@material-ui/icons/Person";
import Table from "@material-ui/core/Table";
import Box from "@material-ui/core/Box";

import NewFiltersBox from "views/FilterBox/NewFiltersBox";
import Button from "components/CustomButtons/Button.js";
import CardHeader from "components/Card/CardHeader.js";
import { isFdaViewEnabled } from "common/helpers.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import Loader from "components/Loader/Loader.js";
import Card from "components/Card/Card.js";

import { downloadBlobFile, arrayToCsvFormat, hasQueryParam } from "common/helpers";
import { API_BASE_URL } from 'common/constants';
import { fetchRequest } from "common/service";

const useStyles = makeStyles((theme) => ({
  cellCheckbox: {padding: '8px', textAlign: 'center'},
  headerCheckbox: {padding: '8px'},
  container: {overflowX: 'auto'},
  table: {minWidth: 800}
}));

const DeviceSettings = () => {
  const userDetails = JSON.parse(localStorage.getItem("user-details"));
  const loggedUserRole = userDetails !== null ? userDetails.roles[0].role : "";

  const isFdaView = isFdaViewEnabled();
  const classes = useStyles();

  const [originalData, setOriginalData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [featureData, setFeatureData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  const [headerStates, setHeaderStates] = useState({
    enableReports: false,
    enableAlerts: false,
    enableNotifications: false,
    enableNightMode: false,
  });

  const fetchData = async (fetchedUserInfo = null) => {
    if(fetchedUserInfo && Object.keys(fetchedUserInfo).length > 0) {
      setUserInfo(fetchedUserInfo);
    }

    if(fetchedUserInfo?.user) {
      try {
        setIsLoading(true);
        /* Identity Table data */
        const endPoint = `${API_BASE_URL}/data/devicedata-advanced/owner?pageNumber=0&pageSize=500&sortBy=maxValueProvidedOn&sortOrder=DESC`;
        
        const payload = {
          "deviceDataModelId": "d36a4373-fbdc-44a3-8c96-4bb920041e40-id",
          "devicePropertyCodes": [ "FriendlyName", "Devid", "EnableAlerts", "EnableReports", "EnableNotifications"],
          "dataTypes": [ "Text", "Text", "Text", "Text", "Text"],
          "devicePropertySetId": "fdab73ae-28df-45f5-92c5-b24460daf682",
          "ownerFilter": {"users": fetchedUserInfo?.user}
        };

        if(fetchedUserInfo?.friendlyName && fetchedUserInfo?.friendlyName !== "all") {
          payload.deviceCriteria = [{"key": "Devid", "operator": "Equal", "value": fetchedUserInfo?.friendlyName, "valueFrom": "", "valueTo": "", "isDateValue": false, "dateTimeRangeValue": null}];
        }
        
        const identityData = await fetchRequest("POST", endPoint, payload);

        /* Params Table data */
        const paramsPayload = {
          "deviceDataModelId": "d36a4373-fbdc-44a3-8c96-4bb920041e40-pa",
          "devicePropertyCodes": ["DevidParams", "NightMode", "NightModeStart", "NightModeEnd"],
          "dataTypes": [ "Text", "Text", "Text", "Text"],
          "devicePropertySetId": "cda7b88f-3f07-43cf-bba9-0512010874fc",
          "ownerFilter": {"users": fetchedUserInfo?.user}
        };
        if(fetchedUserInfo?.friendlyName && fetchedUserInfo?.friendlyName !== "all") {
          paramsPayload.deviceCriteria = [{"key": "DevidParams", "operator": "Equal", "value": fetchedUserInfo?.friendlyName, "valueFrom": "", "valueTo": "", "isDateValue": false, "dateTimeRangeValue": null}];
        }
        
        const paramsData = await fetchRequest("POST", endPoint, paramsPayload);

        const _featureData = identityData?.content?.map(identityItem => {
          const matchingParams = paramsData?.content?.find(paramsItem => 
            paramsItem?.data?.DevidParams?.value === identityItem?.data?.Devid?.value
          );
          
          if (matchingParams) {
            return {
              ...identityItem,
              data: {
                ...identityItem.data,
                paramsDeviceDataId: matchingParams.deviceDataId,
                EnableNightMode: matchingParams.data?.NightMode,
                EnableReports: identityItem.data?.EnableReports,
                EnableAlerts: identityItem.data?.EnableAlerts,
                EnableNotifications: identityItem.data?.EnableNotifications,
                NightModeStart: matchingParams.data?.NightModeStart,
                NightModeEnd: matchingParams.data?.NightModeEnd
              }
            };
          }
          return null;
        }).filter(item => item !== null) || [];

        setFeatureData(_featureData);
        setOriginalData(JSON.parse(JSON.stringify(_featureData))); // Deep copy for comparison
        setIsLoading(false);
      } catch (error) {
        console.error('Error in getLastPVitalsForSensor:', error);
        setIsLoading(false);
      }
    } 
  }

  // Handle individual checkbox changes
  const handleCheckboxChange = (rowIndex, field, checked) => {
    const updatedData = [...featureData];
    let updatedValue;
    if (field === "EnableNotifications") {
      updatedValue = checked ? "Push" : "";
    } else {
      updatedValue = checked ? 'Yes' : 'No';
    }

    if(updatedData[rowIndex].data[field] && updatedData[rowIndex].data[field].value) {
      updatedData[rowIndex].data[field].value = updatedValue;
    } else {
      updatedData[rowIndex].data[field] = {
        valueProvidedOn: new Date().toISOString(),
        value: updatedValue,
        reference: null,
        link: null
      }
    }
    setFeatureData(updatedData);
    
    const hasAnyChanges = updatedData.some((item, index) => {
      const original = originalData[index];
      return original && (
        item.data.EnableReports?.value !== original.data.EnableReports?.value ||
        item.data.EnableAlerts?.value !== original.data.EnableAlerts?.value ||
        item.data.EnableNotifications?.value !== original.data.EnableNotifications?.value ||
        item.data.EnableNightMode?.value !== original.data.EnableNightMode?.value ||
        item.data.NightModeStart?.value !== original.data.NightModeStart?.value ||
        item.data.NightModeEnd?.value !== original.data.NightModeEnd?.value
      );
    });
    setHasChanges(hasAnyChanges);
  };

  // Handle time input changes
  const handleTimeChange = (rowIndex, field, value) => {
    const updatedData = [...featureData];
    const alteredValue = moment(value, 'HH:mm:ss').format("HH:mm:ss");

    if(updatedData[rowIndex].data[field] && updatedData[rowIndex].data[field].value) {
      updatedData[rowIndex].data[field].value = alteredValue;
    } else {
      updatedData[rowIndex].data[field] = {
        valueProvidedOn: new Date().toISOString(),
        value: alteredValue,
        reference: null,
        link: null
      }
    }
    setFeatureData(updatedData);
    
    const hasAnyChanges = updatedData.some((item, index) => {
      const original = originalData[index];
      return original && (
        item.data.EnableReports?.value !== original.data.EnableReports?.value ||
        item.data.EnableAlerts?.value !== original.data.EnableAlerts?.value ||
        item.data.EnableNotifications?.value !== original.data.EnableNotifications?.value ||
        item.data.EnableNightMode?.value !== original.data.EnableNightMode?.value ||
        item.data.NightModeStart?.value !== original.data.NightModeStart?.value ||
        item.data.NightModeEnd?.value !== original.data.NightModeEnd?.value
      );
    });
    setHasChanges(hasAnyChanges);
  };

  // Handle header checkbox changes
  const handleHeaderCheckboxChange = (field, checked) => {
    const updatedData = featureData.map(item => ({
      ...item,
      data: {
        ...item.data,
        [field]: {
          ...item.data[field],
          value: field === 'EnableNotifications' ? (checked ? 'Push' : '') : (checked ? 'Yes' : 'No')
        }
      }
    }));
    
    setFeatureData(updatedData);
    
    // Update header states
    setHeaderStates(prev => ({ 
      ...prev, 
      enableReports: field === 'EnableReports' ? checked : prev.enableReports,
      enableAlerts: field === 'EnableAlerts' ? checked : prev.enableAlerts,
      enableNotifications: field === 'EnableNotifications' ? checked : prev.enableNotifications,
      enableNightMode: field === 'EnableNightMode' ? checked : prev.enableNightMode
    }));
    
    // Check if there are changes
    const hasAnyChanges = updatedData.some((item, index) => {
      const original = originalData[index];
      return original && (
        item.data.EnableReports?.value !== original.data.EnableReports?.value ||
        item.data.EnableAlerts?.value !== original.data.EnableAlerts?.value ||
        item.data.EnableNotifications?.value !== original.data.EnableNotifications?.value ||
        item.data.EnableNightMode?.value !== original.data.EnableNightMode?.value
      );
    });
    setHasChanges(hasAnyChanges);
  };

  // Update header states based on current data
  useEffect(() => {
    if (featureData.length > 0) {
      const allReportsChecked = featureData.every(item => 
        item.data?.EnableReports?.value === 'yes' || item.data?.EnableReports?.value === 'Yes'
      );
      const allAlertsChecked = featureData.every(item => 
        item.data?.EnableAlerts?.value === 'yes' || item.data?.EnableAlerts?.value === 'Yes'
      );
      const allNotificationsChecked = featureData.every(item => 
        item.data?.EnableNotifications?.value === 'Push' || item.data?.EnableNotifications?.value === 'push'
      );
      const allNightModeChecked = featureData.every(item => 
        item.data?.EnableNightMode?.value === 'yes' || item.data?.EnableNightMode?.value === 'Yes'
      );
      
      setHeaderStates({
        enableReports: allReportsChecked,
        enableAlerts: allAlertsChecked,
        enableNotifications: allNotificationsChecked,
        enableNightMode: allNightModeChecked,
      });
    }
  }, [featureData]);

  // Download CSV data
  const downloadCSVData = () => {
    if (featureData.length === 0) {
      alert("No data found");
      return;
    }

    const headers = [
      "User",
      "FriendlyName", 
      "DevID",
      "Reports",
      "Alerts", 
      "Notifications",
      "NightMode",
      "NightModeStart",
      "NightModeEnd"
    ];

    const values = featureData.map(item => ({
      User: userInfo?.fullName || 'N/A',
      FriendlyName: item?.data?.FriendlyName?.value || 'N/A',
      DevID: item?.data?.Devid?.value || 'N/A',
      Reports: item?.data?.EnableReports?.value || 'N/A',
      Alerts: item?.data?.EnableAlerts?.value || 'N/A',
      Notifications: item?.data?.EnableNotifications?.value || 'N/A',
      NightMode: item?.data?.EnableNightMode?.value || 'N/A',
      NightModeStart: item?.data?.NightModeStart?.value ? moment(item?.data?.NightModeStart?.value).format("HH:mm") : 'N/A',
      NightModeEnd: item?.data?.NightModeEnd?.value ? moment(item?.data?.NightModeEnd?.value).format("HH:mm") : 'N/A'
    }));

    downloadBlobFile(
      arrayToCsvFormat(values, headers), 
      `premium_features_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`, 
      "text/csv;charset=utf-8;"
    );
  };

  // Handle save button click
  const handleSave = async() => {
    const changedData = featureData.filter((item, index) => {
      const original = originalData[index];
      return original && (
        item.data.EnableReports?.value !== original.data.EnableReports?.value ||
        item.data.EnableAlerts?.value !== original.data.EnableAlerts?.value ||
        item.data.EnableNotifications?.value !== original.data.EnableNotifications?.value ||
        item.data.EnableNightMode?.value !== original.data.EnableNightMode?.value ||
        item.data.NightModeStart?.value !== original.data.NightModeStart?.value ||
        item.data.NightModeEnd?.value !== original.data.NightModeEnd?.value
      );
    });
    
    console.log(originalData, 'Changed data:', changedData);
    
    if (changedData.length === 0) {
      alert('No changes to save');
      return;
    }

    try {
      // Check if any item has identity parameter changes (EnableAlerts, EnableReports, EnableNotifications)
      const hasIdentityChanges = changedData.some((item, index) => {
        const original = originalData[index];
        return original && (
          item.data?.EnableAlerts?.value !== original.data?.EnableAlerts?.value ||
          item.data?.EnableReports?.value !== original.data?.EnableReports?.value ||
          item.data?.EnableNotifications?.value !== original.data?.EnableNotifications?.value
        );
      });

      let updatePromises = [];
      if (hasIdentityChanges) {
        updatePromises = changedData.map(async (item) => {
          const payload = {
            "deviceDataModelId": "d36a4373-fbdc-44a3-8c96-4bb920041e40-id",
            "devicePropertySetId": "fdab73ae-28df-45f5-92c5-b24460daf682",
            "deviceDataId": item.deviceDataId,
            "data": {
              EnableAlerts: item.data?.EnableAlerts?.value, 
              EnableReports: item.data?.EnableReports?.value, 
              EnableNotifications: item.data?.EnableNotifications?.value,
            }
          };

          const endPoint = `${API_BASE_URL}/data/devicedata`;
          return await fetchRequest("POST", endPoint, payload);
        });
      }

      // Check if any item has NightMode changes
      const hasNightModeChanges = changedData.some((item, index) => {
        const original = originalData[index];
        return original && (
          item.data?.EnableNightMode?.value !== original.data?.EnableNightMode?.value ||
          item.data?.NightModeStart?.value !== original.data?.NightModeStart?.value ||
          item.data?.NightModeEnd?.value !== original.data?.NightModeEnd?.value
        );
      });

      let updatePromisesForParams = [];
      if (hasNightModeChanges) {
        updatePromisesForParams = changedData.map(async (item) => {
          const payload = {
            "deviceDataModelId": "d36a4373-fbdc-44a3-8c96-4bb920041e40-pa",
            "devicePropertySetId": "cda7b88f-3f07-43cf-bba9-0512010874fc",
            "deviceDataId": item?.data?.paramsDeviceDataId || item.deviceDataId,
            "data": {
              NightMode: item.data?.EnableNightMode?.value, 
              NightModeStart: item.data?.NightModeStart?.value, 
              NightModeEnd: item.data?.NightModeEnd?.value
            }
          };

          const endPoint = `${API_BASE_URL}/data/devicedata`;
          return await fetchRequest("POST", endPoint, payload);
        });
      }

      // Wait for all updates to complete
      const allPromises = [...updatePromises, ...updatePromisesForParams];
      const results = await Promise.all(allPromises);
      console.log("All updates completed:", results);
      
      // Show success alert
      alert(`Successfully saved ${changedData.length} changed records!`);
      
      // Update original data after save
      setOriginalData(JSON.parse(JSON.stringify(featureData)));
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes. Please try again.');
    }
  };

  return (
    <div>
      <NewFiltersBox onApply={fetchData} />
      
      {featureData?.length > 0 && (
        <Card>
          <CardHeader color="danger" icon>
            <CardIcon color="danger">
              <Person />
            </CardIcon>
            <Box style={{ float: "right" }}>
              <Button color="danger" disabled={hasChanges} onClick={downloadCSVData} style={{ marginRight: '10px' }}>Download CSV</Button>
              <Button color="primary" disabled={!hasChanges} onClick={handleSave} className={classes.saveButton}>Save Changes</Button>
            </Box>
          </CardHeader>
          <CardBody>
            <TableContainer className={classes.container}>
              <Table stickyHeader aria-label="premium features table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{minWidth: 120}} align="center">
                       <Typography>User</Typography>
                     </TableCell>
                     <TableCell style={{minWidth: 150}} align="center">
                       <Typography>Friendly Name</Typography>
                     </TableCell>
                     <TableCell style={{minWidth: 200}} align="center">
                       <Typography>Dev ID</Typography>
                     </TableCell>
                     {(["TenantAdmin", "TenantUser"].includes(loggedUserRole) || hasQueryParam('showreport')) && <TableCell style={{minWidth: 140}} align="center">
                       <Box display="flex" alignItems="center" justifyContent="center">
                         <Checkbox
                           checked={headerStates.enableReports}
                           onChange={(e) => handleHeaderCheckboxChange('EnableReports', e.target.checked)}
                           className={classes.headerCheckbox}
                         />
                         <Typography>Reports</Typography>
                       </Box>
                     </TableCell>}
                     {!isFdaView && <>
                      <TableCell style={{ minWidth: 140 }} align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Checkbox
                            checked={headerStates.enableAlerts}
                            onChange={(e) => handleHeaderCheckboxChange('EnableAlerts', e.target.checked)}
                            className={classes.headerCheckbox}
                          />
                          <Typography>Alerts</Typography>
                        </Box>
                      </TableCell>
                      <TableCell style={{ minWidth: 160 }} align="center">
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Checkbox
                            checked={headerStates.enableNotifications}
                            onChange={(e) => handleHeaderCheckboxChange('EnableNotifications', e.target.checked)}
                            className={classes.headerCheckbox}
                          />
                          <Typography>Notifications</Typography>
                        </Box>
                      </TableCell>
                    </>}
                     <TableCell style={{minWidth: 140}} align="center">
                       <Box display="flex" alignItems="center" justifyContent="center">
                         <Checkbox
                           checked={headerStates.enableNightMode}
                           onChange={(e) => handleHeaderCheckboxChange('EnableNightMode', e.target.checked)}
                           className={classes.headerCheckbox}
                         />
                         <Typography>Night Mode</Typography>
                       </Box>
                     </TableCell>
                     <TableCell style={{minWidth: 200}} align="center">
                       <Typography>Night Mode Start</Typography>
                     </TableCell>
                     <TableCell style={{minWidth: 200}} align="center">
                       <Typography>Night Mode End</Typography>
                     </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {featureData.map((item, index) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TableCell style={{minWidth: 120, textAlign: "center"}}>
                        {userInfo?.fullName || 'N/A'}
                      </TableCell>
                      <TableCell style={{ minWidth: 150, textAlign: "center", fontWeight: '500'}}>
                        {item?.data?.FriendlyName?.value || 'N/A'}
                      </TableCell>
                      <TableCell style={{ minWidth: 200, textAlign: "center", fontFamily: 'monospace'}}>
                        {item?.data?.Devid?.value || 'N/A'}
                      </TableCell>
                      {(["TenantAdmin", "TenantUser"].includes(loggedUserRole) || hasQueryParam('showreport')) && <TableCell style={{ minWidth: 140, textAlign: "center", }} className={classes.cellCheckbox}>
                        <Checkbox
                          checked={(item?.data?.EnableReports?.value === 'yes' || item?.data?.EnableReports?.value === 'Yes') || false}
                          onChange={(e) => handleCheckboxChange(index, 'EnableReports', e.target.checked)}
                          inputProps={{ "aria-label": "enable reports checkbox" }}
                        />
                      </TableCell>}
                      {!isFdaView && <>
                        <TableCell style={{minWidth: 140, textAlign: "center" }} className={classes.cellCheckbox}>
                          <Checkbox
                            checked={(item?.data?.EnableAlerts?.value === 'yes' || item?.data?.EnableAlerts?.value === 'Yes') || false}
                            onChange={(e) => handleCheckboxChange(index, 'EnableAlerts', e.target.checked)}
                            inputProps={{ "aria-label": "enable notifications checkbox" }}
                          />
                        </TableCell>
                        <TableCell style={{minWidth: 160, textAlign: "center"}} className={classes.cellCheckbox}>
                          <Checkbox
                            checked={(item?.data?.EnableNotifications?.value === 'Push' || item?.data?.EnableNotifications?.value === 'push') || false}
                            onChange={(e) => handleCheckboxChange(index, 'EnableNotifications', e.target.checked)}
                            inputProps={{ "aria-label": "enable notifications checkbox" }}
                          />
                        </TableCell>
                      </>}
                      <TableCell style={{ minWidth: 140, textAlign: "center" }} className={classes.cellCheckbox}>
                        <Checkbox
                          checked={(item?.data?.EnableNightMode?.value === 'yes' || item?.data?.EnableNightMode?.value === 'Yes') || false}
                          onChange={(e) => handleCheckboxChange(index, 'EnableNightMode', e.target.checked)}
                          inputProps={{ "aria-label": "enable night mode checkbox" }}
                        />
                      </TableCell>
                      <TableCell style={{minWidth: 200, textAlign: "center"}}>
                        {(item?.data?.EnableNightMode?.value === 'Yes' || item?.data?.EnableNightMode?.value === 'yes') ? (
                          <TextField
                            type="time"
                            value={item?.data?.NightModeStart?.value || ''}
                            onChange={(e) => handleTimeChange(index, 'NightModeStart', e.target.value)}
                            InputLabelProps={{shrink: true}}
                            inputProps={{style: { textAlign: 'center' }}}
                            style={{ width: '120px' }}
                          />
                        ) : (
                          item?.data?.NightModeStart?.value || 'N/A'
                        )}
                      </TableCell>
                      <TableCell style={{minWidth: 200, textAlign: "center"}}>
                        {(item?.data?.EnableNightMode?.value === 'Yes' || item?.data?.EnableNightMode?.value === 'yes') ? (
                          <TextField
                            type="time"
                            value={item?.data?.NightModeEnd?.value || ''}
                            onChange={(e) => handleTimeChange(index, 'NightModeEnd', e.target.value)}
                            InputLabelProps={{shrink: true}}
                            inputProps={{style: { textAlign: 'center' }}}
                            style={{ width: '120px' }}
                          />
                        ) : (
                          item?.data?.NightModeEnd?.value || 'N/A'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}
      
      {featureData?.length === 0 && !isLoading && userInfo && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '18px',
          fontWeight: 'bold',
          marginTop: '20px',
          color: '#666'
        }}>
          <span>No premium features data found</span>
        </div>
      )}
      
      <Loader status={isLoading} />
    </div>
  );
};

export default DeviceSettings;